import React from 'react';
import styled from 'styled-components';
import { TextInput } from '@raptormaps/text-input';
import { Button } from '@raptormaps/button';
import { Stack, Row } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';
import { Icon } from '@raptormaps/icon';
import theme from '@raptormaps/theme';
import { PartnerFarmLink } from '../../api/services/solar-farm-partners';
import { Partner } from '../../api/services/partners';

const PartnerSelect = styled.select`
  width: 100%;
  padding: 0.5625rem 2rem 0.5625rem 0.75rem;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.neutral_200};
  border-radius: 6px;
  font-size: 0.875rem;
  color: ${theme.colors.neutral_800};
  cursor: pointer;
  appearance: none;
`;

interface PartnerEditFormProps {
  activeLink: PartnerFarmLink | undefined;
  partners: Partner[] | undefined;
  partnersLoading: boolean;
  partnersError: boolean;
  selectedPartnerId: number | null;
  setSelectedPartnerId: (id: number | null) => void;
  partnerFarmKey: string;
  setPartnerFarmKey: (key: string) => void;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function PartnerEditForm({
  activeLink,
  partners,
  partnersLoading,
  partnersError,
  selectedPartnerId,
  setSelectedPartnerId,
  partnerFarmKey,
  setPartnerFarmKey,
  saving,
  canSave,
  onCancel,
  onSave,
}: PartnerEditFormProps) {
  return (
    <Stack gutter="md">
      {activeLink ? (
        <Stack gutter="xxs">
          <Text variant="label_small_allcaps" color="#718096">
            Partner
          </Text>
          <Text variant="paragraph_medium">{activeLink.partner.name}</Text>
        </Stack>
      ) : partnersError ? (
        <Row gutter="xs" align="center">
          <Icon icon="TriangleExclamation" size="small" color="#dc2626" />
          <Text variant="paragraph_small" color="#dc2626">
            Failed to load partners. Please refresh.
          </Text>
        </Row>
      ) : (
        <Stack gutter="xxs">
          <Text
            as="label"
            variant="label_small_allcaps"
            color="#718096"
            htmlFor="partner-select"
          >
            Partner
          </Text>
          <PartnerSelect
            id="partner-select"
            value={selectedPartnerId ?? ''}
            onChange={e =>
              setSelectedPartnerId(
                e.target.value ? Number(e.target.value) : null,
              )
            }
            disabled={partnersLoading}
          >
            <option value="">
              {partnersLoading ? 'Loading...' : 'Select a partner...'}
            </option>
            {partners?.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </PartnerSelect>
        </Stack>
      )}
      <TextInput
        label="Partner Key"
        value={partnerFarmKey}
        onChange={e => setPartnerFarmKey(e.target.value)}
        placeholder="Enter partner key..."
      />
      <Row gutter="sm">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave} disabled={!canSave}>
          {saving ? 'Saving...' : activeLink ? 'Update Partner' : 'Add Partner'}
        </Button>
      </Row>
    </Stack>
  );
}
